import React from 'react';
import { Table, Box, Button, SpaceBetween, StatusIndicator } from '@cloudscape-design/components';
import { formatDistanceToNow } from 'date-fns';
import { SharedView } from 'src/components/views/ShareView';
import { SharedViewStatus } from '@amzn/controllershiphub-typescript-client';

interface SharedViewsTableProps {
  sharedViews: SharedView[];
  onAccept: (view: SharedView) => void;
  onDecline: (view: SharedView) => void;
  loading: boolean;
}

// Table to Display shared views
export const SharedViewsTable: React.FC<SharedViewsTableProps> = ({
  sharedViews,
  onAccept,
  onDecline,
  loading,
}) => {
  const columnDefinitions = [
    {
      id: 'viewName',
      header: 'View Name',
      cell: (item: SharedView) => item.viewName,
      sortingField: 'viewName',
    },
    {
      id: 'sharedBy',
      header: 'Shared By',
      cell: (item: SharedView) => item.sharedBy,
      sortingField: 'sourceUserAlias',
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item: SharedView) => item.description || '-',
      sortingField: 'description',
    },
    {
      id: 'sharedTimestamp',
      header: 'Shared Timestamp',
      cell: (item: SharedView) => formatDistanceToNow(item.sharedTimestamp, { addSuffix: true }),
      sortingField: 'sharedOn',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: SharedView) => (
        <StatusIndicator type={getStatusIndicatorType(item.status)}>{item.status}</StatusIndicator>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item: SharedView) =>
        item.status === SharedViewStatus.PENDING && (
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => onAccept(item)}>Accept</Button>
            <Button onClick={() => onDecline(item)} variant="link">
              Decline
            </Button>
          </SpaceBetween>
        ),
    },
  ];

  // Build Status Indicator based on the current shared view status
  const getStatusIndicatorType = (status: SharedViewStatus) => {
    switch (status) {
      case SharedViewStatus.PENDING:
        return 'pending';
      case SharedViewStatus.ACCEPTED:
        return 'success';
      case SharedViewStatus.DECLINED:
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Table
      loading={loading}
      items={sharedViews}
      columnDefinitions={columnDefinitions}
      onSelectionChange={({ detail }) => {
        console.log('Selected item viewId:', detail.selectedItems[0]?.viewId);
      }}
      header={<h2>Shared Views</h2>}
      empty={
        <Box textAlign="center" color="inherit">
          <b>No shared views</b>
          <Box padding={{ bottom: 's' }} variant="p" color="inherit">
            No one has shared any views with you yet.
          </Box>
        </Box>
      }
    />
  );
};
